import { useEffect, useState } from "react";
import moment from "moment";
import { Button, Spinner } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import { useApiResource } from "../../hooks/useApiResource";
import type {
  LatestEpicRun
} from "../../types/latestEpicRuns";
import { analytics } from "../../utils/analytics";
import { getEpicColor } from "../../utils/epicColors";
import RunParticipants from "../epics/RunParticipants";
import SectionHeader from "../common/SectionHeader";
import SkeletonBlock from "../common/SkeletonBlock";
import BossChips from "../epicLeaderboard/BossChips";
import EpicRunNote from "../epicLeaderboard/EpicRunNote";
import "../epicLeaderboard/epicLeaderboard.css";
import "./LatestEpicRuns.css";

const COLLAPSED_COUNT = 5;
const EXPANDED_COUNT = 25;
// Relative times ("12 min ago") re-render this often.
const TICK_MS = 60 * 1000;

// Per-viewer memory of whether the section is open; collapsed by default.
const OPEN_KEY = "home-latest-epic-runs-open";

const readOpen = (): boolean => {
  try {
    return window.localStorage.getItem(OPEN_KEY) === "1";
  } catch {
    return false;
  }
};

const writeOpen = (open: boolean) => {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? "1" : "0");
  } catch {
    // Storage blocked (private mode etc.) - the toggle still works.
  }
};

const latestUrl = (limit: number) =>
  `${process.env.REACT_APP_API}public/epic-runs/latest?limit=${limit}`;

const fullDate = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short"
  });

type RowProps = {
  run: LatestEpicRun;
  open: boolean;
  onToggle: () => void;
};

const RunRow = ({ run, open, onToggle }: RowProps) => {
  const panelId = `latest-epic-run-${run.id}`;
  const showExtras = run.smallGroup;
  return (
    <li className="latest-epics-item">
      <button
        type="button"
        className="latest-epics-row"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="latest-epics-title">
          <span
            className="latest-epics-dot"
            style={{ backgroundColor: getEpicColor(run.epicName) }}
            aria-hidden="true"
          />
          <span className="latest-epics-name">{run.epicName}</span>
        </span>
        <span className="latest-epics-meta">
          <span className="latest-epics-time" title="Clear time">
            {run.totalTime ?? "—"}
          </span>
          <span className="latest-epics-count">
            {run.totalPlayers} {run.totalPlayers === 1 ? "player" : "players"}
          </span>
          <span className="latest-epics-organizer">
            {run.organizer ? `by ${run.organizer}` : ""}
          </span>
          <time
            className="latest-epics-when"
            dateTime={run.runDate}
            title={fullDate(run.runDate)}
          >
            {moment(run.runDate).fromNow()}
          </time>
        </span>
        <span className="latest-epics-chevron" aria-hidden="true" />
      </button>
      {showExtras && (
        <div className="latest-epics-extras">
          <BossChips bosses={run.bosses} />
          <EpicRunNote runId={run.id} note={run.note} />
        </div>
      )}
      {open && (
        <div id={panelId} className="latest-epics-participants">
          <RunParticipants runId={run.id} />
        </div>
      )}
    </li>
  );
};

const SkeletonRows = () => (
  <div aria-hidden="true">
    {Array.from({ length: COLLAPSED_COUNT }).map((_, i) => (
      <div key={i} className="latest-epics-skeleton-row">
        <SkeletonBlock width={12} height={12} style={{ borderRadius: "50%" }} />
        <SkeletonBlock width="40%" height={16} />
        <SkeletonBlock width="25%" height={14} style={{ marginLeft: "auto" }} />
      </div>
    ))}
  </div>
);

// The newest finished epic runs, collapsed until opened (nothing is fetched
// before that): five at first, 25 on "Show more" (kept in memory, so
// collapsing and expanding again doesn't refetch). Click a run to see who was
// on it.
const LatestEpicRuns = () => {
  const [sectionOpen, setSectionOpen] = useState(readOpen);
  const [expanded, setExpanded] = useState(false);
  // The 25-run request only starts once the user asks for it.
  const [wantMore, setWantMore] = useState(false);
  const [openRunId, setOpenRunId] = useState<number | null>(null);
  const [, setTick] = useState(0);

  const initial = useApiResource<LatestEpicRun[]>(
    sectionOpen ? latestUrl(COLLAPSED_COUNT) : null,
    "home-latest-epic-runs"
  );
  const more = useApiResource<LatestEpicRun[]>(
    wantMore ? latestUrl(EXPANDED_COUNT) : null,
    "home-latest-epic-runs"
  );

  useEffect(() => {
    if (!sectionOpen) return undefined;
    const interval = setInterval(() => setTick((n) => n + 1), TICK_MS);
    return () => clearInterval(interval);
  }, [sectionOpen]);

  const toggleSection = () =>
    setSectionOpen((open) => {
      writeOpen(!open);
      analytics.trackFeatureUsage(
        "LatestEpicRuns",
        open ? "collapse_section" : "open_section"
      );
      return !open;
    });

  const showMore = () => {
    analytics.trackFeatureUsage("LatestEpicRuns", "show_more");
    setWantMore(true);
    setExpanded(true);
  };

  const runs =
    expanded && more.data
      ? more.data
      : (initial.data ?? []).slice(0, COLLAPSED_COUNT);
  const loadingMore = expanded && wantMore && more.loading && !more.data;
  const moreFailed = expanded && wantMore && !more.data && more.error !== null;
  // Fewer than five runs came back, so there is nothing more to show.
  const canExpand = (initial.data?.length ?? 0) >= COLLAPSED_COUNT;

  const toggleRun = (id: number) =>
    setOpenRunId((current) => (current === id ? null : id));

  let body;
  if (!initial.data && !initial.error) {
    body = <SkeletonRows />;
  } else if (initial.error && !initial.data) {
    body = (
      <div className="latest-epics-status" role="alert">
        <span className="me-2">Couldn't load recent epic runs.</span>
        <Button size="sm" variant="outline-light" onClick={initial.reload}>
          Retry
        </Button>
      </div>
    );
  } else if (runs.length === 0) {
    body = <div className="latest-epics-status">No epic runs yet.</div>;
  } else {
    body = (
      <ul className="latest-epics-list">
        {runs.map((run) => (
          <RunRow
            key={run.id}
            run={run}
            open={openRunId === run.id}
            onToggle={() => toggleRun(run.id)}
          />
        ))}
      </ul>
    );
  }

  const showFooter = initial.data !== null && canExpand;

  return (
    <section aria-label="Recently completed epics">
      <SectionHeader
        title={
          <button
            type="button"
            className="latest-epics-section-toggle"
            aria-expanded={sectionOpen}
            aria-controls="latest-epics-body"
            onClick={toggleSection}
          >
            RECENTLY COMPLETED EPICS
            <span
              className={`latest-epics-section-chevron${
                sectionOpen ? " latest-epics-section-chevron--open" : ""
              }`}
              aria-hidden="true"
            />
          </button>
        }
        action={
          <NavLink to="/epics-dashboard" className="section-link">
            All runs &rarr;
          </NavLink>
        }
      />
      {sectionOpen && (
      <div id="latest-epics-body" className="latest-epics-card">
        {body}
        {showFooter && (
          <div className="latest-epics-footer">
            {moreFailed ? (
              <>
                <span className="me-2 align-self-center">
                  Couldn't load more runs.
                </span>
                <Button size="sm" variant="outline-light" onClick={more.reload}>
                  Retry
                </Button>
              </>
            ) : expanded ? (
              <Button
                size="sm"
                variant="link"
                disabled={loadingMore}
                onClick={() => setExpanded(false)}
              >
                {loadingMore ? (
                  <>
                    <Spinner animation="border" size="sm" variant="light" />
                    <span className="visually-hidden">Loading more runs</span>
                  </>
                ) : (
                  "Show less"
                )}
              </Button>
            ) : (
              <Button size="sm" variant="link" onClick={showMore}>
                Show more
              </Button>
            )}
          </div>
        )}
      </div>
      )}
    </section>
  );
};

export default LatestEpicRuns;
